import { 
    ref, 
    push, 
    set, 
    onValue, 
    off, 
    query, 
    orderByChild, 
    equalTo,
    serverTimestamp 
} from "firebase/database";
import { database } from "./config";

class ChatService {
    constructor() {
        this.database = database;
        this.listeners = new Map(); // لتخزين listeners وإزالتها
    }

    // إنشاء محادثة جديدة
    async createConversation(senderId, receiverId) {
        try {
            const conversationRef = ref(this.database, 'conversations');
            const newConversationRef = push(conversationRef);
            
            const conversationData = {
                id: newConversationRef.key,
                senderId,
                receiverId,
                createdAt: serverTimestamp(),
                lastMessage: null,
                lastMessageTime: null
            };

            await set(newConversationRef, conversationData);
            return newConversationRef.key;
        } catch (error) {
            console.error('Error creating conversation:', error);
            throw error;
        }
    }

    // إرسال رسالة
    async sendMessage(conversationId, senderId, message) {
        try {
            const messageRef = ref(this.database, `conversations/${conversationId}/messages`);
            const newMessageRef = push(messageRef);
            
            const messageData = {
                id: newMessageRef.key,
                senderId,
                message,
                timestamp: serverTimestamp(),
                read: false
            };

            await set(newMessageRef, messageData);

            // تحديث آخر رسالة في المحادثة
            const conversationRef = ref(this.database, `conversations/${conversationId}`);
            await set(ref(this.database, `conversations/${conversationId}/lastMessage`), message);
            await set(ref(this.database, `conversations/${conversationId}/lastMessageTime`), serverTimestamp());

            return newMessageRef.key;
        } catch (error) {
            console.error('Error sending message:', error);
            throw error;
        }
    }

    // جلب محادثة مع شخص معين
    async getConversation(senderId, receiverId) {
        try {
            const conversationsRef = ref(this.database, 'conversations');
            const conversationQuery = query(
                conversationsRef,
                orderByChild('senderId'),
                equalTo(senderId)
            );

            return new Promise((resolve, reject) => {
                onValue(conversationQuery, (snapshot) => {
                    const conversations = [];
                    snapshot.forEach((childSnapshot) => {
                        const conversation = childSnapshot.val();
                        if (conversation.receiverId === receiverId || 
                            (conversation.receiverId === senderId && conversation.senderId === receiverId)) {
                            conversations.push(conversation);
                        }
                    });
                    
                    if (conversations.length > 0) {
                        resolve(conversations[0]);
                    } else {
                        resolve(null);
                    }
                }, (error) => {
                    reject(error);
                });
            });
        } catch (error) {
            console.error('Error getting conversation:', error);
            throw error;
        }
    }

    // الاستماع للرسائل في محادثة معينة (real-time)
    listenToMessages(conversationId, callback) {
        const messagesRef = ref(this.database, `conversations/${conversationId}/messages`);
        
        // إزالة listener سابق إذا كان موجود
        if (this.listeners.has(conversationId)) {
            off(messagesRef, 'value', this.listeners.get(conversationId));
        }

        const listener = onValue(messagesRef, (snapshot) => {
            const messages = [];
            snapshot.forEach((childSnapshot) => {
                messages.push(childSnapshot.val());
            });
            
            // ترتيب الرسائل حسب الوقت
            messages.sort((a, b) => {
                if (a.timestamp && b.timestamp) {
                    return a.timestamp - b.timestamp;
                }
                return 0;
            });
            
            callback(messages);
        });

        // حفظ الـ listener لإزالتته لاحقاً
        this.listeners.set(conversationId, listener);
        
        return listener;
    }

    // إزالة listener
    stopListeningToMessages(conversationId) {
        if (this.listeners.has(conversationId)) {
            const messagesRef = ref(this.database, `conversations/${conversationId}/messages`);
            off(messagesRef, 'value', this.listeners.get(conversationId));
            this.listeners.delete(conversationId);
        }
    }

    // جلب جميع المحادثات للمستخدم
    async getUserConversations(userId) {
        try {
            const conversationsRef = ref(this.database, 'conversations');
            const conversationsQuery = query(
                conversationsRef,
                orderByChild('senderId'),
                equalTo(userId)
            );

            return new Promise((resolve, reject) => {
                onValue(conversationsQuery, (snapshot) => {
                    const conversations = [];
                    snapshot.forEach((childSnapshot) => {
                        conversations.push(childSnapshot.val());
                    });
                    resolve(conversations);
                }, (error) => {
                    reject(error);
                });
            });
        } catch (error) {
            console.error('Error getting user conversations:', error);
            throw error;
        }
    }

    // تحديث حالة قراءة الرسالة
    async markMessageAsRead(conversationId, messageId) {
        try {
            await set(ref(this.database, `conversations/${conversationId}/messages/${messageId}/read`), true);
        } catch (error) {
            console.error('Error marking message as read:', error);
            throw error;
        }
    }

    // إزالة جميع listeners
    cleanup() {
        this.listeners.forEach((listener, conversationId) => {
            this.stopListeningToMessages(conversationId);
        });
    }
}

export default new ChatService();
